import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'state/actions';
import selectors from 'state/selectors';
import { StyledMarkdown } from '../Content/styles';
import ReactMarkdown from 'react-markdown';
import {
  WalkthroughSvg,
  SpotlightDot,
  StepCounter,
  Tooltip,
  TooltipActions,
  TooltipDescription,
  TooltipTitle,
} from './styles';
import { CommonButton } from 'components/Buttons';
import { useTargetPosition } from './hooks/useTargetPosition';
import { useWalkthroughEvents } from './hooks/useWalkthroughEvents';
import utils from 'utils';
import { calculateTooltipPosition } from './utils/positioning';

const PAD = 5;

const WalkthroughOverlay: React.FC = () => {
  const dispatch = useDispatch();
  const walkthrough = useSelector(selectors.chatbot.getWalkthroughState);
  const currentStep = useSelector(selectors.chatbot.getCurrentWalkthroughStep);

  const t = utils.intl.useTranslator();
  const { targetRect, portalRect } = useTargetPosition(currentStep?.uiTarget || '', walkthrough.isActive);

  const handleNextStep = () => dispatch(actions.chatbot.nextWalkthroughStep());
  useWalkthroughEvents(currentStep, walkthrough.isActive, handleNextStep);

  const skipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (skipTimerRef.current) {
      clearTimeout(skipTimerRef.current);
      skipTimerRef.current = null;
    }

    if (currentStep?.optional && walkthrough.isActive) {
      skipTimerRef.current = setTimeout(() => {
        const targets = currentStep.uiTarget.split('|').map((t) => t.trim());
        const isPresent = targets.some((target) => !!document.querySelector(`[data-walkthrough="${target}"]`));
        if (!isPresent) {
          if (currentStep.end) {
            dispatch(actions.chatbot.closeWalkthrough());
          } else {
            dispatch(actions.chatbot.nextWalkthroughStep());
          }
        }
      }, 400);
    }

    return () => {
      if (skipTimerRef.current) {
        clearTimeout(skipTimerRef.current);
        skipTimerRef.current = null;
      }
    };
  }, [currentStep, walkthrough.isActive, dispatch]);

  if (!walkthrough.isActive || !currentStep || !targetRect) return null;

  const { tooltipLeft, tooltipTop, tooltipPosition } = calculateTooltipPosition(targetRect, portalRect);
  const currentStepIndex = walkthrough.steps.findIndex((s) => s.id === walkthrough.currentStepId);
  const stepNumber = currentStepIndex + 1;
  const totalSteps = walkthrough.steps.length;

  const handleNext = () => {
    if (currentStep?.end) {
      dispatch(actions.chatbot.closeWalkthrough());
    } else {
      dispatch(actions.chatbot.nextWalkthroughStep());
    }
  };

  const handleClose = () => dispatch(actions.chatbot.closeWalkthrough());

  return (
    <>
      <WalkthroughSvg>
        <defs>
          <mask id="walkthrough-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - PAD}
              y={targetRect.top - PAD}
              width={targetRect.width + PAD * 2}
              height={targetRect.height + PAD * 2}
              rx={4}
              ry={4}
              fill="black"
            />
            {portalRect && (
              <rect
                x={portalRect.left - PAD}
                y={portalRect.top - PAD}
                width={portalRect.width + PAD * 2}
                height={portalRect.height + PAD * 2}
                rx={4}
                ry={4}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.7)" mask="url(#walkthrough-mask)" />
      </WalkthroughSvg>
      <SpotlightDot top={targetRect.top - 6} left={targetRect.right - 3} />
      <Tooltip key={currentStep.id} top={tooltipTop} left={tooltipLeft} position={tooltipPosition}>
        <StepCounter>
          {stepNumber} / {totalSteps}
        </StepCounter>
        <TooltipTitle>{currentStep.title}</TooltipTitle>
        <TooltipDescription>
          <StyledMarkdown>
            <ReactMarkdown>{currentStep.description}</ReactMarkdown>
          </StyledMarkdown>
        </TooltipDescription>
        <TooltipActions>
          {!currentStep.end && (
            <CommonButton
              action={handleClose}
              id="close-walkthrough"
              value={utils.app.textTranslated('close', t, 'Close')}
              scope="secondary"
            />
          )}
          <CommonButton
            value={
              currentStep.end
                ? utils.app.textTranslated('finish', t, 'Finish')
                : utils.app.textTranslated('next', t, 'Next')
            }
            action={handleNext}
            id={`${currentStep.end ? 'finish-walkthrough' : 'next-walkthrough'}`}
          />
        </TooltipActions>
      </Tooltip>
    </>
  );
};

export default WalkthroughOverlay;
